/**
 * Shared types for Blaxel mount operations.
 */

import type { SandboxInstance } from '@blaxel/core';

export const LOG_PREFIX = '[@mastra/blaxel]';

import type { BlaxelGCSMountConfig } from './gcs';
import type { BlaxelS3MountConfig } from './s3';

/**
 * Union of mount configs supported by Blaxel sandbox.
 */
export type BlaxelMountConfig = BlaxelS3MountConfig | BlaxelGCSMountConfig;

/**
 * Context for mount operations.
 */
export interface MountContext {
  sandbox: SandboxInstance;
  logger: {
    debug: (message: string, ...args: unknown[]) => void;
    info: (message: string, ...args: unknown[]) => void;
    warn: (message: string, ...args: unknown[]) => void;
    error: (message: string, ...args: unknown[]) => void;
  };
}

const SAFE_S3_BUCKET_NAME = /^[a-z0-9][a-z0-9.\-]{1,61}[a-z0-9]$/;
const SAFE_GCS_BUCKET_NAME = /^[a-z0-9][a-z0-9._-]{1,61}[a-z0-9]$/;

export function validateS3BucketName(bucket: string): void {
  if (!SAFE_S3_BUCKET_NAME.test(bucket)) {
    throw new Error(
      `Invalid S3 bucket name: "${bucket}". Bucket names must be 3-63 characters, lowercase alphanumeric, hyphens, or dots.`,
    );
  }
}

export function validateGCSBucketName(bucket: string): void {
  if (!SAFE_GCS_BUCKET_NAME.test(bucket)) {
    throw new Error(
      `Invalid GCS bucket name: "${bucket}". Bucket names must be 3-63 characters, lowercase alphanumeric, hyphens, underscores, or dots.`,
    );
  }
}

/**
 * Validate an endpoint URL before interpolating into shell commands.
 * Only http and https schemes are allowed.
 */
export function validateEndpoint(endpoint: string): void {
  let parsed: URL;
  try {
    parsed = new URL(endpoint);
  } catch {
    throw new Error(`Invalid endpoint URL: "${endpoint}"`);
  }
  if (parsed.protocol !== 'http:' && parsed.protocol !== 'https:') {
    throw new Error(`Invalid endpoint URL scheme: "${parsed.protocol}". Only http: and https: are allowed.`);
  }
}

/**
 * Validate and normalize a mount prefix before interpolating into shell commands.
 * Returns the normalized prefix (no leading/trailing slashes).
 *
 * Shell safety is handled by shellQuote() at the call site, so this function
 * only enforces path-level rules (no traversal, no empty result, no control chars).
 */
export function validatePrefix(prefix: string): string {
  // Trim leading/trailing slashes
  let normalized = prefix;
  while (normalized.startsWith('/')) normalized = normalized.slice(1);
  while (normalized.endsWith('/')) normalized = normalized.slice(0, -1);

  if (!normalized) {
    throw new Error('Mount prefix cannot be empty after normalization.');
  }
  if (normalized.includes('//') || normalized.split('/').some(s => s === '.' || s === '..')) {
    throw new Error(`Invalid mount prefix: "${prefix}". Path traversal is not allowed.`);
  }
  // Block control characters (U+0000–U+001F, U+007F) which are invalid in filesystem paths
  if (/[\x00-\x1f\x7f]/.test(normalized)) {
    throw new Error(`Invalid mount prefix: "${prefix}". Control characters are not allowed.`);
  }
  return normalized;
}

/**
 * Detected system package manager.
 */
export type PackageManager = 'apt' | 'apk' | 'unknown';

/**
 * Detect the system package manager available in the sandbox.
 * Returns 'apt' for Debian/Ubuntu, 'apk' for Alpine, or 'unknown'.
 */
export async function detectPackageManager(sandbox: SandboxInstance): Promise<PackageManager> {
  const result = await runCommand(
    sandbox,
    'which apt-get >/dev/null 2>&1 && echo "apt" || (which apk >/dev/null 2>&1 && echo "apk" || echo "unknown")',
  );
  const pm = result.stdout.trim();
  if (pm === 'apt') return 'apt';
  if (pm === 'apk') return 'apk';
  return 'unknown';
}

/**
 * Run a command in the Blaxel sandbox and return the result.
 * Wraps the process.exec API to match the command execution pattern used in mount operations.
 *
 * Does NOT throw on non-zero exit codes — callers should check `exitCode` themselves.
 */
export async function runCommand(
  sandbox: SandboxInstance,
  command: string,
  options?: { timeout?: number },
): Promise<{ exitCode: number; stdout: string; stderr: string }> {
  const result = await sandbox.process.exec({
    command,
    waitForCompletion: true,
    ...(options?.timeout && { timeout: Math.ceil(options.timeout / 1000) }),
  });

  return {
    exitCode: result.exitCode ?? 0,
    stdout: result.stdout ?? '',
    stderr: result.stderr ?? '',
  };
}
